import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from '@store/app.state';
import { FrameActions } from '@store/actions/frame.actions';
import { selectCanUndo, selectCanRedo, selectAllFrames } from '@store/selectors/frame.selectors';
import { CollaborationService } from '@app/services/collaboration.service';
import { PixelFrame } from '@models/frame.model';

@Component({
  selector: 'app-edit-menu',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule],
  templateUrl: './edit-menu.html',
  styleUrls: ['./edit-menu.css']
})
export class EditMenu implements OnInit, OnDestroy {
  isOpen = false;
  fxOpen = false;
  showTweenModal = false;
  canUndo$!: Observable<boolean>;
  canRedo$!: Observable<boolean>;
  allFrames$!: Observable<PixelFrame[]>;

  // Tween state
  tweenFromId: string | null = null;
  tweenToId: string | null = null;
  tweenSteps = 4;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    public collabSvc: CollaborationService,
  ) {
    this.canUndo$ = this.store.select(selectCanUndo);
    this.canRedo$ = this.store.select(selectCanRedo);
    this.allFrames$ = this.store.select(selectAllFrames);
  }

  ngOnInit(): void {
    this.store.select(selectAllFrames).pipe(takeUntil(this.destroy$)).subscribe(frames => {
      if (frames.length >= 2 && !this.tweenFromId) {
        this.tweenFromId = frames[0].id;
        this.tweenToId   = frames[frames.length > 1 ? 1 : 0].id;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isSpectator(): boolean { return this.collabSvc.role$.value === 'spectator'; }

  toggle(): void {
    if (this.isSpectator) return;
    this.isOpen = !this.isOpen;
    if (!this.isOpen) this.fxOpen = false;
  }

  close(): void {
    this.isOpen = false;
    this.fxOpen = false;
  }

  openFx(): void { this.fxOpen = true; }
  closeFx(): void { this.fxOpen = false; }

  openTweenModal(): void {
    this.showTweenModal = true;
    this.close();
  }

  closeTweenModal(): void { this.showTweenModal = false; }

  undo(): void {
    this.store.dispatch(this.collabSvc.isInRoom$.value ? FrameActions.undoCollab() : FrameActions.undo());
    this.close();
  }

  redo(): void {
    this.store.dispatch(this.collabSvc.isInRoom$.value ? FrameActions.redoCollab() : FrameActions.redo());
    this.close();
  }

  tweenFrames(): void {
    if (!this.tweenFromId || !this.tweenToId) return;
    this.store.dispatch(FrameActions.tweenFrames({
      fromFrameId: this.tweenFromId, toFrameId: this.tweenToId, steps: this.tweenSteps
    }));
    this.closeTweenModal();
  }
}
